import { useState, useEffect, Fragment } from 'react';
import { locationShape, routerShape } from 'react-router';
import { replace } from 'react-router-redux';
import { connect } from 'react-redux';
import lodash from 'lodash';
import {
    SubFormRouter,
    SubFormRoute
} from 'forms/containers/SubFormRouter/SubFormRouter';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import {
    Container,
    FormSection,
} from 'forms/components/common';
import { Recipient } from 'forms/components/Events/Recipient';
import { SigningIframe } from 'part11/components/SigningIframe/SigningIframe';
import {
    signEventTask,
    getEventTask,
    unloadEventTask,
    cancelEventSignature,
} from 'forms/actions/events';

import LoadingComponent from 'common/permissions/components/LoadingComponent';
import EventDetails from 'forms/components/Events/EventDetails';
import CustomEventDetails from 'forms/components/Events/CustomEventDetails';

import { getAuthCredentials } from 'common/util/auth-storage';
import { openPopup, listenForCredentials } from 'common/util/popup';
import { fetchSignUrl } from 'common/actions/login';

export function EventTask(props) {
    const [taskActionSent, setTaskActionSent] = useState(false);

    useEffect(() => {
        props.getEventTask(props.signatureRequestId);

        return () => {
            props.unloadEventTask();
        };
    }, []);

    useEffect(() => {
        if (props.submissionSuccess) {
            const { replace, location } = props;
            const newLocation = lodash.cloneDeep(location);
            newLocation.pathname = '/inbox';
            replace(newLocation);
        }
    }, [props.submissionSuccess]);

    const getSigningUrl = () => {
        const accessToken = getAuthCredentials().accessToken;
        return `${props.originUrl}/sign?access_token=${accessToken}&action_text=Sign Event and Complete Task`;
    };

    const onSignEventSignatureRequest = (signingToken) => {
        const {
            signatureRequest,
        } = props;
        const eventId = signatureRequest.event ? signatureRequest.event.id : signatureRequest.customEvent.id;
        const institutionId = signatureRequest.event ? signatureRequest.event.institutionId : signatureRequest.customEvent.institutionId;

        setTaskActionSent(true);

        props.signEventTask({
            eventId: eventId,
            taskId: lodash.get(signatureRequest, 'id', null),
            institutionId: institutionId,
            signingToken,
        });
    };

    const { signatureRequest, taskBox, userEmailAddress } = props;
    const signatureRequestEmailAddress = lodash.get(props, 'signatureRequest.requestedFrom.emailAddress', '');
    const permissionToSignTask = userEmailAddress === signatureRequestEmailAddress;
    const signingUrl = getSigningUrl();
    const isLoading = lodash.isNil(signatureRequest);
    const isSending = taskActionSent;

    return (
        <Fragment data-cy="EventTask">
            <LoadingSpinner
                loading={isLoading}
                sending={isSending}
            />

            { (!isLoading && !isSending) && (
                <SubFormRouter>
                    <SubFormRoute>
                        <Container>
                            <EventDetailsSection
                                taskBox={taskBox}
                                signatureRequest={signatureRequest}
                            />

                            <TaskSigningIframe
                                signingUrl={signingUrl}
                                permissionToSignTask={permissionToSignTask}
                                onSignEventSignatureRequest={onSignEventSignatureRequest.bind(this)}
                                taskBox={taskBox}
                                addApplicationMessage={props.addApplicationMessage}
                                isSSOSigningEnabled={Boolean(lodash.get(props.featureAccess, 'ssoESign'))}
                                isSsoUser={lodash.get(props.user, 'isSso', false)}
                                originUrl={props.originUrl}
                            />

                            <RecipientSection
                                signatureRequest={signatureRequest}
                                taskBox={taskBox}
                                cancelEventSignature={props.cancelEventSignature}
                            />
                        </Container>
                    </SubFormRoute>
                </SubFormRouter>
            )}
        </Fragment>
    );
}

const LoadingSpinner = ({ loading, sending }) => {
    if (!loading && !sending) {
        return null;
    }

    let action = 'Loading';
    if (sending) {
        action = 'Completing';
    }

    const text = `${action} task ...`;
    return <LoadingComponent text={text} />;
};

const EventDetailsSection = ({ signatureRequest, taskBox }) => {
    if (lodash.isNil(signatureRequest.event)) {
        return (
            <CustomEventDetails
                taskBox={taskBox}
                signatureRequest={signatureRequest}
            />
        );
    }

    return (
        <EventDetails
            taskBox={taskBox}
            signatureRequest={signatureRequest}
        />
    );
};

const RecipientSection = ({ signatureRequest, taskBox, cancelEventSignature }) => {
    if (taskBox === 'sent') {
        return (
            <Recipient
                taskId={signatureRequest.id}
                displayName={signatureRequest.requestedFrom.displayName}
                emailAddress={signatureRequest.requestedFrom.emailAddress}
                status={signatureRequest.status}
                cancelTask={() => cancelEventSignature()}
            />
        );
    }

    return null;
};

const TaskSigningIframe = ({
    signingUrl,
    onSignEventSignatureRequest,
    permissionToSignTask,
    taskBox,
    addApplicationMessage,
    isSSOSigningEnabled,
    isSsoUser,
    originUrl,
}) => {
    if (taskBox === 'sent') {
        return null;
    }
    if (permissionToSignTask) {
        if (isSSOSigningEnabled && isSsoUser) {
            const accessToken = getAuthCredentials().accessToken;
            return (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
                    <button
                        style={{
                            backgroundColor: '#2b5ea7', // color--blue1 equivalent or secondary-color
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                        onClick={() => {
                            fetchSignUrl(accessToken, true, originUrl).then(res => {
                                const popup = openPopup(res.provider_uri, 'sign');
                                listenForCredentials(popup)
                                    .then(res => onSignEventSignatureRequest(res.token))
                                    .catch(err => addApplicationMessage('error', err.message || 'SSO authentication failed.'));
                            });
                        }}>
                        Authenticate with SSO to Sign
                    </button>
                </div>
            );
        }
        return (
            <FormSection data-cy="EventTask-TaskSigningIframe">
                <SigningIframe
                    url={signingUrl}
                    signDocument={onSignEventSignatureRequest} />
            </FormSection>
        );
    } else {
        addApplicationMessage('error', 'You are not permitted to sign this task.');
        return null;
    }
};

EventTask.contextTypes = {
    router: routerShape.isRequired,
    location: locationShape.isRequired
};

export default connect((state, props) => {
    const userEmailAddress = lodash.get(state, 'me.email', '');
    const signatureRequest = lodash.get(state, 'form.signatureRequest', null);
    const signatureRequestId = lodash.get(props, 'params.id');
    const taskBox = lodash.get(props, 'location.query.taskBox', 'inbox');
    const originUrl = lodash.get(state, 'application.config.corsOriginUrl', null);
    const submissionSuccess = lodash.get(state, 'form.submissionSuccess', null);

    return {
        signatureRequest,
        originUrl,
        submissionSuccess,
        signatureRequestId,
        taskBox,
        userEmailAddress,
        user: state.me,
        featureAccess: lodash.get(state, `me.featureAccess.${lodash.get(state, 'me.institution.id')}`, {}),
    };
}, {
    replace,
    signEventTask,
    getEventTask,
    unloadEventTask,
    cancelEventSignature,
    addApplicationMessage,
})(EventTask);
